import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

import { DialogProvider } from '@hapana/shared/web/components/shared/dialog/dialog.provider'
import NavigationGuard from '_components/navigation-guard'
import SettingsGuard from '_components/settings-guard'
import SiteUrlSync from '_components/site-url-sync'
import SupportAssistantShell from '_components/support-assistant/support-assistant-shell'
import { SupportAssistantProvider } from '_components/support-assistant/support-assistant.context'
import KBarProviderComponent from '_components/kbar/kbar-provider.component'
import { HapanaLoader } from '@hapana/shared/web/components/shared/loading/variants/hapana/hapana-loader.component'
import { ROUTES } from './app.constants'

function HelpDefaultRedirect() {
  const VALID_TABS = ['search', 'chats', 'support', 'docs', 'activity']
  const lastTab = localStorage.getItem('hapana-help-last-tab')
  const tab = VALID_TABS.includes(lastTab) ? lastTab : 'search'
  return <Navigate to={tab} replace />
}

// Critical path components - loaded immediately
import Layout from '_pages/layout/layout.component'
import Login from '_pages/login/login.component'
import LoginAs from '_pages/login-as/login-as.component'
import RequireAuth from '_pages/require-route-auth/require-route-auth.component'
import RootErrorBoundary from '_pages/root-error-boundary/root-error-boundary.component'

// Lazy-loaded page components - loaded on demand
const Dashboard = lazy(() => import('_pages/dashboard/dashboard.component'))
const SpannerDashboard = lazy(() => import('_pages/dashboard/spanner-dashboard.component'))
const Clients = lazy(() => import('_pages/clients/clients.component'))
const ClientOverview = lazy(() => import('_pages/clients/client-overview.component'))
const CorporateLookup = lazy(
  () => import('_pages/clients/components/corporate-lookup/corporate-lookup.component')
)
const UserAccess = lazy(() => import('_pages/user-access/user-access.component'))
const UserProfile = lazy(() => import('_pages/user-profile/user-profile.component'))
const SiteTeamAccess = lazy(() => import('_pages/site-team-access/site-team-access.component'))
const LocationSelection = lazy(
  () => import('_pages/location-selection/location-selection.component')
)
const Schedule = lazy(() => import('_pages/schedule/schedule.component'))
const Payments = lazy(() => import('_pages/payments/payments.component'))
const Reports = lazy(() => import('_pages/reports/reports.component'))
const MemberView = lazy(() => import('_pages/member-mirror/member-mirror.page'))
const Analytics = lazy(() => import('_pages/analytics/analytics.component'))
const ControlCenter = lazy(() => import('_pages/control-center/control-center.component'))
const Settings = lazy(() => import('_pages/settings/settings.component'))
const LocationSettings = lazy(() => import('_pages/location-settings/location-settings.component'))
const RoomResourceConfiguration = lazy(() =>
  import('_pages/settings/location-settings/location-configuration/components/business-locations-config/components/room-resource-configuration.component').then(
    (m) => ({ default: m.RoomResourceConfiguration })
  )
)
const SchedulerSettings = lazy(
  () => import('_pages/scheduler-settings/scheduler-settings.component.jsx')
)
const RetailSettings = lazy(() => import('_pages/retail-settings/retail-settings.component'))
const ZapierConnect = lazy(() => import('_pages/zapier-connect/zapier-connect.component'))
const SiteCommunicationTemplates = lazy(
  () =>
    import('_pages/location-communication-templates/components/templates/communication-templates/site-communication-templates.component')
)
const AccountSettings = lazy(() => import('_pages/account-settings/account-settings.component'))
const WellnessSettings = lazy(() => import('_pages/wellness-settings/wellness-settings.component'))
const CommunicationTemplates = lazy(
  () => import('_pages/communication-templates/communication-templates.component')
)
const FAQs = lazy(() => import('_pages/faqs/faqs.component'))
const Integrations = lazy(() => import('_pages/integrations/integrations.component'))
const ApiIntegrations = lazy(
  () => import('_pages/control-center/api-integrations/api-integrations.component')
)
const AuditHistory = lazy(
  () => import('_pages/control-center/audit-history/audit-history.component')
)
const Onboarding = lazy(() => import('_pages/control-center/onboarding/onboarding.component'))
const MigrationOverview = lazy(() => import('_pages/control-center/onboarding/MigrationOverview'))
const Setup = lazy(() => import('_pages/setup'))
const EventTracker = lazy(() => import('_pages/event-tracker/event-tracker.component'))
const DynamicLoaderDemo = lazy(
  () => import('_pages/dynamic-loader-demo/dynamic-loader-demo.component')
)
const ValentineTest = lazy(() => import('_pages/valentine-test'))
const MobileSetup = lazy(() => import('_pages/mobile-setup'))
const HelpDocs = lazy(() => import('_pages/help-docs/help-docs.component'))
const Search = lazy(() => import('_pages/search/search.component'))
const AssistantLayout = lazy(() => import('_pages/assistant/assistant-layout.component'))
const AssistantChatPage = lazy(() => import('_pages/assistant/assistant-chat-page.component'))
const SupportPage = lazy(() => import('_pages/assistant/support-page.component'))
const DocsPage = lazy(() => import('_pages/assistant/docs-page.component'))
const ActivityPage = lazy(() => import('_pages/assistant/activity-page.component'))
const ErrorBoundaryTest = lazy(
  () => import('_pages/error-boundary-test/error-boundary-test.component')
)
const Retail = lazy(() => import('_pages/retail/retail.component'))
const NewSale = lazy(() => import('_pages/retail-settings/new-sale/new-sale.component'))
const Checkout = lazy(() => import('_pages/retail-settings/checkout/checkout.component'))
const PaymentDetail = lazy(
  () => import('_pages/retail/components/payment-detail/payment-detail.component')
)
const Invoice = lazy(() => import('_pages/retail/components/invoice/invoice.component'))
const RecurringPaymentDetail = lazy(
  () =>
    import('_pages/retail/components/recurring-payment-detail/recurring-payment-detail.component')
)
const FailedPayments = lazy(() => import('_pages/retail/components/failed-payments'))
const DraftInvoices = lazy(() => import('_pages/retail/components/draft-invoices'))
const AcceptInvitation = lazy(() => import('_pages/accept-invitation/accept-invitation.component'))
const SelectBrand = lazy(() => import('_pages/select-brand/select-brand.component'))
const WaitlistConfirm = lazy(() => import('_pages/waitlist-confirm/waitlist-confirm.component'))
const PublicInvoice = lazy(() => import('_pages/public-invoice/public-invoice.component'))
const PublicAgreement = lazy(() => import('_pages/public-agreement/public-agreement.component'))
const BarcodeCheckIn = lazy(() => import('_pages/barcode-checkin/barcode-checkin.component'))
const PublicAgreementV2 = lazy(
  () => import('_pages/public-agreement-v2/public-agreement.component')
)
const ResetPasswordPage = lazy(() => import('_pages/reset-password/reset-password-page.component'))
const PosthogDebug = lazy(() => import('_pages/posthog-debug/posthog-debug.component'))
const DataImport = lazy(() => import('_pages/data-import/data-import.component'))
const ContentSuite = lazy(() => import('_pages/content-suite/content-suite.page'))
const ContentEditor = lazy(() => import('_pages/content-suite/editor/content-editor.page'))
const MediaLibrary = lazy(() => import('_pages/content-suite/media/media-library.page'))
const ContentSuiteAnalytics = lazy(
  () => import('_pages/content-suite/analytics/content-analytics.page')
)
const FCMDebug = lazy(() => import('_pages/fcm-debug/fcm-debug.component'))

// Demo pages (public, no auth required)
const DemoSignup = lazy(() => import('_pages/demo/demo-signup.component'))
const DemoVerify = lazy(() => import('_pages/demo/demo-verify.component'))
const DemoCreating = lazy(() => import('_pages/demo/demo-creating.component'))
const DemoMagicLinkCallback = lazy(() => import('_pages/demo/demo-magic-link-callback.component'))

const PageLoader = () => (
  <div
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}
  >
    <HapanaLoader size={24} />
  </div>
)

// Helper to wrap lazy components with Suspense
// Shows a loading spinner while chunks are fetched (important on slow networks / Windows antivirus scanning)
// The page itself handles its own loading states for data
const withSuspense = (Component) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
)

export const ROOT_PATH = ''

const CatchAllRoute = () => {
  return <Navigate to="/login" replace />
}

const AppLayout = () => {
  return (
    <SupportAssistantProvider>
      <SupportAssistantShell>
        <KBarProviderComponent>
          <DialogProvider>
            <Layout />
          </DialogProvider>
        </KBarProviderComponent>
      </SupportAssistantShell>
    </SupportAssistantProvider>
  )
}

const LANDING_PAGE_ROUTES = {
  index: true,
  element: withSuspense(Dashboard),
}

const USER_ACCESS_ROUTES = {
  path: ROUTES.userAccess.path,
  element: <Navigate to="users" replace />,
}

const USER_ACCESS_TABS_ROUTES = {
  path: ROUTES.userAccess.children.userAccessTabs.path,
  element: withSuspense(UserAccess),
}

const SITE_TEAM_ACCESS_ROUTES = {
  path: ROUTES.siteTeamAccess.path,
  element: withSuspense(SiteTeamAccess),
}

const USER_PROFILE_ROUTES = {
  path: ROUTES.userProfile.path,
  element: withSuspense(UserProfile),
}

const LOCATION_SELECTION_ROUTES = {
  path: ROUTES.locationSelection.path,
  element: withSuspense(LocationSelection),
}

const CLIENTS_ROUTES = {
  path: ROUTES.clients.path,
  element: withSuspense(Clients),
}

const CLIENT_OVERVIEW_ROUTES = {
  path: ROUTES.clients.children.clientOverview.path,
  element: withSuspense(ClientOverview),
}

const MOBILE_SETUP_ROUTES = {
  path: ROUTES.mobileSetup.path,
  element: withSuspense(MobileSetup),
}

const RETAIL_ROUTES = {
  path: ROUTES.retail.path,
  element: withSuspense(Retail),
  children: [
    {
      index: true,
      element: <Navigate to="payment-log" replace />,
    },
    {
      path: 'payment-log',
      element: withSuspense(Retail),
    },
    {
      path: 'memberships',
      element: withSuspense(Retail),
    },
    {
      path: 'packages',
      element: withSuspense(Retail),
    },
    {
      path: 'scheduled',
      element: withSuspense(Retail),
    },
    {
      path: 'pos',
      element: withSuspense(Retail),
    },
    {
      path: 'gift-card',
      element: withSuspense(Retail),
    },
    {
      path: 'disputes',
      element: withSuspense(Retail),
    },
    {
      path: 'pay-as-you-go',
      element: withSuspense(Retail),
    },
  ],
}

const SCHEDULE_ROUTES = {
  path: ROUTES.schedule.path,
  element: withSuspense(Schedule),
}

const PAYMENTS_ROUTES = {
  path: ROUTES.payments.path,
  element: withSuspense(Payments),
}

const REPORTS_ROUTES = {
  path: ROUTES.reports.path,
  element: withSuspense(Reports),
}

const MEMBER_VIEW_ROUTES = {
  path: ROUTES.memberView.path,
  element: withSuspense(MemberView),
}

const ANALYTICS_ROUTES = {
  path: ROUTES.analytics.path,
  element: withSuspense(Analytics),
}

const CORPORATE_LOOKUP_ROUTES = {
  path: 'clients/lookup',
  element: withSuspense(CorporateLookup),
}

const CONTROL_CENTER_ROUTES = {
  path: ROUTES.controlCenter.path,
  element: withSuspense(ControlCenter),
}

const SETTINGS_ROUTES = {
  path: ROUTES.settings.path,
  element: withSuspense(Settings),
}

const LOCATION_SETTINGS_ROUTES = {
  path: ROUTES.settings.children.locationSettings.path,
  element: withSuspense(LocationSettings),
}

const LOCATION_SETTINGS_TABS_ROUTES = {
  path: ROUTES.settings.children.locationSettings.children.locationSettingsTabs.path,
  element: withSuspense(LocationSettings),
}

const ROOM_RESOURCE_CONFIGURATION_ROUTES = {
  path: ROUTES.settings.children.locationSettings.children.roomResourceConfiguration.path,
  element: withSuspense(RoomResourceConfiguration),
}
const LOCATION_COMMUNICATION_TEMPLATES_ROUTES = {
  path: ROUTES.settings.children.locationSettings.children.siteCommunicationTemplates.path,
  element: withSuspense(SiteCommunicationTemplates),
}

const SCHEDULER_SETTINGS_ROUTES = {
  path: ROUTES.settings.children.schedulerSettings.path,
  element: withSuspense(SchedulerSettings),
}

const SCHEDULER_SETTINGS_TABS_ROUTES = {
  path: ROUTES.settings.children.schedulerSettings.children.schedulerSettingsTabs.path,
  element: withSuspense(SchedulerSettings),
}

const RETAIL_SETTINGS_ROUTES = {
  path: ROUTES.settings.children.retailSettings.path,
  element: withSuspense(RetailSettings),
}

const RETAIL_SETTINGS_TABS_ROUTES = {
  path: ROUTES.settings.children.retailSettings.children.retailSettingsTabs.path,
  element: withSuspense(RetailSettings),
}

const NEW_SALE_ROUTES = {
  path: ROUTES.retailDashboard.children.newSale.path,
  element: withSuspense(NewSale),
}

const NEW_SALE_TABS_ROUTES = {
  path: ROUTES.retailDashboard.children.newSale.children.newSaleTabs.path,
  element: withSuspense(NewSale),
}

const CHECKOUT_ROUTES = {
  path: ROUTES.retailDashboard.children.checkout.path,
  element: withSuspense(Checkout),
}

const PAYMENT_DETAIL_ROUTES = {
  path: ROUTES.retailDashboard.children.paymentDetail.path,
  element: withSuspense(PaymentDetail),
}

const RECURRING_PAYMENT_DETAIL_ROUTES = {
  path: ROUTES.retailDashboard.children.recurringPaymentDetail.path,
  element: withSuspense(RecurringPaymentDetail),
}

const INVOICE_ROUTES = {
  path: ROUTES.retailDashboard.children.invoice.path,
  element: withSuspense(Invoice),
}

const FAILED_PAYMENTS_ROUTES = {
  path: ROUTES.retailDashboard.children.failed.path,
  element: withSuspense(FailedPayments),
}

const DRAFT_INVOICES_ROUTES = {
  path: ROUTES.retailDashboard.children.draftInvoices.path,
  element: withSuspense(DraftInvoices),
}

const ACCOUNT_SETTINGS_ROUTES = {
  path: ROUTES.settings.children.accountSettings.path,
  element: withSuspense(AccountSettings),
}

const ACCOUNT_SETTINGS_TABS_ROUTES = {
  path: ROUTES.settings.children.accountSettings.children.accountSettingsTabs.path,
  element: withSuspense(AccountSettings),
}

const WELLNESS_SETTINGS_ROUTES = {
  path: ROUTES.settings.children.wellnessSettings.path,
  element: withSuspense(WellnessSettings),
}

const WELLNESS_SETTINGS_TABS_ROUTES = {
  path: ROUTES.settings.children.wellnessSettings.children.wellnessSettingsTabs.path,
  element: withSuspense(WellnessSettings),
}

const COMMUNICATION_TEMPLATES_ROUTES = {
  path: ROUTES.controlCenter.children.communicationTemplates.path,
  element: withSuspense(CommunicationTemplates),
}

const FAQS_ROUTES = {
  path: ROUTES.controlCenter.children.faqs.path,
  element: withSuspense(FAQs),
}

const INTEGRATIONS_ROUTES = {
  path: ROUTES.controlCenter.children.integrations.path,
  element: withSuspense(Integrations),
}

const API_INTEGRATIONS_ROUTES = {
  path: ROUTES.controlCenter.children.apiIntegrations.path,
  element: <Navigate to="integrations" replace />,
}

const API_INTEGRATIONS_TABS_ROUTES = {
  path: ROUTES.controlCenter.children.apiIntegrations.children.apiIntegrationsTabs.path,
  element: withSuspense(ApiIntegrations),
}

const AUDIT_HISTORY_ROUTES = {
  path: ROUTES.controlCenter.children.auditHistory.path,
  element: withSuspense(AuditHistory),
}

const ONBOARDING_ROUTES = {
  path: ROUTES.controlCenter.children.onboarding.path,
  element: withSuspense(MigrationOverview),
}

const MIGRATION_WIZARD_ROUTES = {
  path: ROUTES.controlCenter.children.onboarding.children.migrationWizard.path,
  element: withSuspense(Onboarding),
}

const SELF_SERVICE_ROUTES = {
  path: ROUTES.controlCenter.children.selfService.path,
  element: withSuspense(Setup),
}

const EVENT_TRACKER_ROUTES = {
  path: ROUTES.product.children.eventTracker.path,
  element: withSuspense(EventTracker),
}

const DYNAMIC_LOADER_DEMO_ROUTES = {
  path: ROUTES.product.children.dynamicLoaderDemo.path,
  element: withSuspense(DynamicLoaderDemo),
}

const VALENTINE_TEST_ROUTES = {
  path: 'product/valentine-test',
  element: withSuspense(ValentineTest),
}

const HELP_DOCS_ROUTES = {
  path: ROUTES.helpDocs.path,
  element: withSuspense(HelpDocs),
}

const HELP_DOCS_ARTICLE_ROUTES = {
  path: ROUTES.helpDocs.children.article.path,
  element: withSuspense(HelpDocs),
}

const CONTENT_SUITE_ROUTES = {
  path: ROUTES.contentSuite.path,
  element: withSuspense(ContentSuite),
}

const CONTENT_SUITE_MEDIA_ROUTES = {
  path: ROUTES.contentSuite.children.mediaLibrary.path,
  element: withSuspense(MediaLibrary),
}

const CONTENT_SUITE_ANALYTICS_ROUTES = {
  path: ROUTES.contentSuite.children.analytics.path,
  element: withSuspense(ContentSuiteAnalytics),
}

const CONTENT_SUITE_NEW_ROUTES = {
  path: 'content-suite/new',
  element: withSuspense(ContentEditor),
}

const CONTENT_SUITE_ENTRY_ROUTES = {
  path: ROUTES.contentSuite.children.contentEntry.path,
  element: withSuspense(ContentEditor),
}

const ASSISTANT_ROUTES = {
  path: ROUTES.assistant.path,
  element: withSuspense(AssistantLayout),
  children: [
    { index: true, element: <HelpDefaultRedirect /> },
    { path: 'search', element: withSuspense(Search) },
    { path: 'chats', element: withSuspense(AssistantChatPage) },
    { path: 'support', element: withSuspense(SupportPage) },
    { path: 'docs', element: withSuspense(DocsPage) },
    { path: 'activity', element: withSuspense(ActivityPage) },
  ],
}

const ACCEPT_INVITATION_ROUTES = {
  path: 'p/accept-invitation/:token',
  element: withSuspense(AcceptInvitation),
}

const WAITLIST_CONFIRM_ROUTES = {
  path: 'p/waitlist/:token',
  element: withSuspense(WaitlistConfirm),
}

const PUBLIC_INVOICE_ROUTES = {
  path: 'p/invoice/:token',
  element: withSuspense(PublicInvoice),
}

const PUBLIC_AGREEMENT_ROUTES = {
  path: 'p/agreement/:token',
  element: withSuspense(PublicAgreement),
}

const BARCODE_CHECK_IN_ROUTES = {
  path: 'p/barcode/:siteId',
  element: withSuspense(BarcodeCheckIn),
}

const PUBLIC_AGREEMENT_V2_ROUTES = {
  path: 'p/agreement-v2/:token',
  element: withSuspense(PublicAgreementV2),
}

const ERROR_BOUNDARY_TEST_ROUTES = {
  path: 'error-boundary-test',
  element: withSuspense(ErrorBoundaryTest),
}

const APP_ROUTES = {
  path: '/',
  element: <AppLayout />,
  errorElement: <RootErrorBoundary />,
  children: [
    {
      path: 'login',
      element: <Login />,
    },
    {
      path: 'login-as',
      element: <LoginAs />,
    },
    {
      path: 'select-brand',
      element: withSuspense(SelectBrand),
    },
    {
      element: <RequireAuth />,
      children: [
        // FCM Debug — accessible without site selection
        { path: 'fcm-debug', element: withSuspense(FCMDebug) },

        {
          element: <NavigationGuard />,
          children: [
            // Zapier OAuth connect page — guard skipped via SKIP_GUARD_PATHS
            {
              path: ROUTES.zapierConnect.path,
              element: withSuspense(ZapierConnect),
            },

            // Landing page (Dashboard) - now protected
            LANDING_PAGE_ROUTES,

            // PLG routes
            { path: 'import', element: withSuspense(DataImport) },

            // Utility routes
            USER_PROFILE_ROUTES,
            LOCATION_SELECTION_ROUTES,

            // Corporate-level routes - /c/*
            // These routes don't require a siteId (brand-wide functionality)
            {
              path: 'c',
              children: [
                // Dashboard as default/index route for corporate
                LANDING_PAGE_ROUTES,

                USER_PROFILE_ROUTES,
                USER_ACCESS_ROUTES,
                USER_ACCESS_TABS_ROUTES,
                CONTROL_CENTER_ROUTES,
                REPORTS_ROUTES,
                ANALYTICS_ROUTES,
                MEMBER_VIEW_ROUTES,
                COMMUNICATION_TEMPLATES_ROUTES,
                FAQS_ROUTES,
                INTEGRATIONS_ROUTES,
                API_INTEGRATIONS_ROUTES,
                API_INTEGRATIONS_TABS_ROUTES,
                AUDIT_HISTORY_ROUTES,
                ONBOARDING_ROUTES,
                MIGRATION_WIZARD_ROUTES,
                SELF_SERVICE_ROUTES,
                EVENT_TRACKER_ROUTES,
                { path: 'posthog', element: withSuspense(PosthogDebug) },
                CONTENT_SUITE_ROUTES,
                CONTENT_SUITE_MEDIA_ROUTES,
                CONTENT_SUITE_ANALYTICS_ROUTES,
                CONTENT_SUITE_NEW_ROUTES,
                CONTENT_SUITE_ENTRY_ROUTES,
                HELP_DOCS_ROUTES,
                HELP_DOCS_ARTICLE_ROUTES,
                ASSISTANT_ROUTES,
              ],
            },

            // Site-level routes - /s/:siteId/*
            // These routes require a specific siteId in the path
            {
              path: 's/:siteId',
              element: <SiteUrlSync />,
              children: [
                // Dashboard as default/index route for site
                LANDING_PAGE_ROUTES,

                // Temporary: Spanner-powered dashboard on /home-2
                { path: ROUTES.home2.path, element: withSuspense(SpannerDashboard) },

                // Utility routes accessible from site context
                USER_PROFILE_ROUTES,

                CLIENTS_ROUTES,
                CLIENT_OVERVIEW_ROUTES,
                CORPORATE_LOOKUP_ROUTES,
                MOBILE_SETUP_ROUTES,
                SITE_TEAM_ACCESS_ROUTES,
                SCHEDULE_ROUTES,
                PAYMENTS_ROUTES,
                REPORTS_ROUTES,
                ANALYTICS_ROUTES,
                MEMBER_VIEW_ROUTES,
                RETAIL_ROUTES,
                NEW_SALE_ROUTES,
                NEW_SALE_TABS_ROUTES,
                CHECKOUT_ROUTES,
                PAYMENT_DETAIL_ROUTES,
                RECURRING_PAYMENT_DETAIL_ROUTES,
                INVOICE_ROUTES,
                FAILED_PAYMENTS_ROUTES,
                DRAFT_INVOICES_ROUTES,
                { path: 'import', element: withSuspense(DataImport) },
                CONTENT_SUITE_ROUTES,
                CONTENT_SUITE_MEDIA_ROUTES,
                CONTENT_SUITE_ANALYTICS_ROUTES,
                CONTENT_SUITE_NEW_ROUTES,
                CONTENT_SUITE_ENTRY_ROUTES,
                HELP_DOCS_ROUTES,
                HELP_DOCS_ARTICLE_ROUTES,
                ASSISTANT_ROUTES,

                // Site-level settings routes with dedicated guard
                {
                  element: <SettingsGuard />,
                  children: [
                    SETTINGS_ROUTES,
                    LOCATION_SETTINGS_ROUTES,
                    LOCATION_SETTINGS_TABS_ROUTES,
                    ROOM_RESOURCE_CONFIGURATION_ROUTES,
                    LOCATION_COMMUNICATION_TEMPLATES_ROUTES,
                    SCHEDULER_SETTINGS_ROUTES,
                    SCHEDULER_SETTINGS_TABS_ROUTES,
                    RETAIL_SETTINGS_ROUTES,
                    RETAIL_SETTINGS_TABS_ROUTES,
                    ACCOUNT_SETTINGS_ROUTES,
                    ACCOUNT_SETTINGS_TABS_ROUTES,
                    WELLNESS_SETTINGS_ROUTES,
                    WELLNESS_SETTINGS_TABS_ROUTES,
                  ],
                },
              ],
            },
          ],
        },
      ],
    },

    // Public routes (with app layout)
    DYNAMIC_LOADER_DEMO_ROUTES,
    VALENTINE_TEST_ROUTES,
    ERROR_BOUNDARY_TEST_ROUTES,

    // Catch all
    {
      path: '*',
      element: <CatchAllRoute />,
    },
  ],
}

const RESET_PASSWORD_ROUTES = {
  path: 'reset-password',
  element: withSuspense(ResetPasswordPage),
}

// Demo routes (public, no auth required — only served on demo.* subdomain)
// Routes are top-level (/signup, /verify, etc.) since the subdomain already scopes them
const DEMO_SIGNUP_ROUTES = { path: 'signup', element: withSuspense(DemoSignup) }
const DEMO_VERIFY_ROUTES = { path: 'verify', element: withSuspense(DemoVerify) }
const DEMO_CREATING_ROUTES = { path: 'creating', element: withSuspense(DemoCreating) }
const DEMO_MAGIC_LINK_ROUTES = { path: 'magic-link', element: withSuspense(DemoMagicLinkCallback) }

// Public routes without app layout (no sidebar/navigation)
// Each route gets its own error boundary so chunk-load failures auto-reload
const PUBLIC_ROUTES = [
  ACCEPT_INVITATION_ROUTES,
  WAITLIST_CONFIRM_ROUTES,
  PUBLIC_INVOICE_ROUTES,
  PUBLIC_AGREEMENT_ROUTES,
  PUBLIC_AGREEMENT_V2_ROUTES,
  BARCODE_CHECK_IN_ROUTES,
  RESET_PASSWORD_ROUTES,
  DEMO_SIGNUP_ROUTES,
  DEMO_VERIFY_ROUTES,
  DEMO_CREATING_ROUTES,
  DEMO_MAGIC_LINK_ROUTES,
].map((route) => ({ ...route, errorElement: <RootErrorBoundary /> }))

/** @type {import('react-router-dom').Router} */
const router = createBrowserRouter([...PUBLIC_ROUTES, APP_ROUTES])

export default router
